import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Plus,
  Loader2,
  Star,
  ArrowLeft,
  Trash2,
  Mail,
  Phone,
  Link as LinkIcon,
  Instagram,
  Youtube,
  Facebook,
  Twitch,
  Video,
  Globe,
  Pencil,
  X,
  Clapperboard,
  Calendar,
  Users,
  Utensils,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DateTimeInput } from "@/components/ui/datetime-input";

export const PLATFORMS = [
  { key: "instagram", label: "Instagram", icon: Instagram, tone: "text-pink-600" },
  { key: "tiktok", label: "TikTok", icon: Video, tone: "text-slate-900" },
  { key: "youtube", label: "YouTube", icon: Youtube, tone: "text-red-600" },
  { key: "facebook", label: "Facebook", icon: Facebook, tone: "text-blue-600" },
  { key: "twitch", label: "Twitch", icon: Twitch, tone: "text-violet-600" },
  { key: "x", label: "X (Twitter)", icon: Globe, tone: "text-slate-900" },
  { key: "egyeb", label: "Egyéb", icon: Globe, tone: "text-slate-500" },
];
const PLATFORM_BY_KEY = Object.fromEntries(PLATFORMS.map((p) => [p.key, p]));

const formatDateTimeHu = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString("hu-HU", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return String(v); }
};

// ---------- List page ----------
export default function InfluencersPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/influencers");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Betöltés sikertelen.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      (it.full_name || "").toLowerCase().includes(q) ||
      (it.email || "").toLowerCase().includes(q) ||
      (it.primary_platform_label || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8" data-testid="influencers-page">
      <section className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Influenszerek</h1>
          <p className="mt-1 text-sm text-slate-500">
            Influenszer partnerek nyilvántartása + forgatások ütemezése.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Keresés..."
            className="h-9 w-56"
            data-testid="influencers-search"
          />
          <Button data-testid="new-influencer-button" onClick={() => setOpenCreate(true)} className="gap-1.5">
            <Plus className="h-4 w-4" /> Új influenszer
          </Button>
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-sm">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="border-b border-slate-200/70 bg-slate-50/60 text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em]">Név</th>
              <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em]">Legerősebb platform</th>
              <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em]">E-mail</th>
              <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em]">Telefon</th>
              <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em]">Forgatások</th>
              <th className="px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em]">Linkek</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={6} className="p-8 text-center text-slate-400">
                <Loader2 className="mx-auto h-5 w-5 animate-spin" />
              </td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} className="p-12 text-center text-slate-400">
                <Star className="mx-auto h-8 w-8 text-slate-300" />
                <p className="mt-2 text-sm">Még nincs influenszer.</p>
              </td></tr>
            ) : filtered.map((it) => {
              const platform = PLATFORM_BY_KEY[it.primary_platform];
              const Icon = platform?.icon || Globe;
              return (
                <tr
                  key={it._id}
                  data-testid={`influencer-row-${it._id}`}
                  onClick={() => navigate(`/influencers/${it._id}`)}
                  className="cursor-pointer hover:bg-slate-50"
                >
                  <td className="px-4 py-3 font-medium text-slate-800">{it.full_name}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 text-slate-700">
                      <Icon className={`h-3.5 w-3.5 ${platform?.tone || ""}`} />
                      {it.primary_platform_label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{it.email || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 tabular-nums">{it.phone || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 tabular-nums">{it.shoot_count}</td>
                  <td className="px-4 py-3 text-slate-500">{it.platform_links?.length || 0} link</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <InfluencerFormDialog
        open={openCreate}
        onOpenChange={setOpenCreate}
        onSaved={() => { setOpenCreate(false); load(); }}
      />
    </div>
  );
}

// ---------- Form dialog (create + edit) ----------
function InfluencerFormDialog({ open, onOpenChange, initial, onSaved }) {
  const isEdit = Boolean(initial);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [primary, setPrimary] = useState("");
  const [links, setLinks] = useState([]); // {platform, url}
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFullName(initial?.full_name || "");
    setEmail(initial?.email || "");
    setPhone(initial?.phone || "");
    setPrimary(initial?.primary_platform || "");
    setLinks(initial?.platform_links || []);
    setNotes(initial?.notes || "");
  }, [open, initial]);

  const addLink = () => setLinks([...links, { platform: "", url: "" }]);
  const removeLink = (idx) => setLinks(links.filter((_, i) => i !== idx));
  const updateLink = (idx, k, v) =>
    setLinks(links.map((l, i) => (i === idx ? { ...l, [k]: v } : l)));

  const submit = async (e) => {
    e.preventDefault();
    if (!fullName.trim()) { toast.error("A név kötelező."); return; }
    if (!primary) { toast.error("Válassz legerősebb platformot."); return; }
    const cleanedLinks = links.filter((l) => l.platform && l.url.trim());
    for (const l of cleanedLinks) {
      if (!l.url.trim()) {
        toast.error("A platform link URL nem lehet üres."); return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        full_name: fullName.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        primary_platform: primary,
        platform_links: cleanedLinks,
        notes: notes.trim() || null,
      };
      if (isEdit) {
        await api.patch(`/influencers/${initial._id}`, payload);
        toast.success("Influenszer frissítve.");
      } else {
        await api.post("/influencers", payload);
        toast.success("Influenszer létrehozva.");
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Mentés sikertelen.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="influencer-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Influenszer szerkesztése" : "Új influenszer"}</DialogTitle>
          <DialogDescription>
            Add meg a partner adatait és a platformokra mutató linkeket.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-600">Teljes név *</label>
              <Input value={fullName} onChange={(e) => setFullName(e.target.value)} data-testid="influencer-name" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-600">Legerősebb platform *</label>
              <Select value={primary} onValueChange={setPrimary}>
                <SelectTrigger data-testid="influencer-primary-platform">
                  <SelectValue placeholder="Válassz platformot..." />
                </SelectTrigger>
                <SelectContent>
                  {PLATFORMS.map((p) => (
                    <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-600">E-mail</label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="influencer-email" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-600">Telefon</label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="influencer-phone" />
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-xs font-medium text-slate-600">Platform linkek</label>
              <Button type="button" size="sm" variant="ghost" onClick={addLink} data-testid="add-link">
                <Plus className="mr-1 h-3.5 w-3.5" /> Új link
              </Button>
            </div>
            <div className="space-y-2">
              {links.length === 0 && (
                <p className="text-xs text-slate-400">Még nincs link hozzáadva.</p>
              )}
              {links.map((l, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`link-row-${idx}`}>
                  <Select value={l.platform} onValueChange={(v) => updateLink(idx, "platform", v)}>
                    <SelectTrigger className="h-9 w-44">
                      <SelectValue placeholder="Platform..." />
                    </SelectTrigger>
                    <SelectContent>
                      {PLATFORMS.map((p) => (
                        <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={l.url}
                    onChange={(e) => updateLink(idx, "url", e.target.value)}
                    placeholder="https://..."
                    className="h-9 flex-1"
                  />
                  <button
                    type="button"
                    onClick={() => removeLink(idx)}
                    className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-600">Megjegyzés</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Pl. együttműködési feltételek, kapcsolattartó megjegyzések..."
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Mégse</Button>
            <Button type="submit" disabled={submitting} data-testid="influencer-submit">
              {submitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              {isEdit ? "Mentés" : "Létrehozás"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Detail page ----------
export function InfluencerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openEdit, setOpenEdit] = useState(false);
  const [openShoot, setOpenShoot] = useState(false);
  const [editingShoot, setEditingShoot] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/influencers/${id}`);
      setItem(data);
    } catch {
      toast.error("Influenszer nem található.");
      navigate("/influencers");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [id]);

  const removeInfluencer = async () => {
    if (!window.confirm(`Biztosan törlöd "${item.full_name}" influenszert?`)) return;
    try {
      await api.delete(`/influencers/${id}`);
      toast.success("Influenszer törölve.");
      navigate("/influencers");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Törlés sikertelen.");
    }
  };

  const removeShoot = async (shootId) => {
    if (!window.confirm("Biztosan törlöd ezt a forgatást?")) return;
    try {
      await api.delete(`/influencers/${id}/shoots/${shootId}`);
      toast.success("Forgatás törölve.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Törlés sikertelen.");
    }
  };

  if (loading || !item) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const primary = PLATFORM_BY_KEY[item.primary_platform];
  const PrimaryIcon = primary?.icon || Globe;

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8" data-testid="influencer-detail-page">
      <button
        onClick={() => navigate("/influencers")}
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"
      >
        <ArrowLeft className="h-4 w-4" /> Vissza
      </button>

      <section className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-pink-100 to-violet-100 text-slate-700">
                <Star className="h-5 w-5" />
              </div>
              <div>
                <h1 className="text-xl font-semibold tracking-tight">{item.full_name}</h1>
                <p className="mt-0.5 inline-flex items-center gap-1.5 text-sm text-slate-500">
                  <PrimaryIcon className={`h-3.5 w-3.5 ${primary?.tone || ""}`} />
                  {item.primary_platform_label}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setOpenEdit(true)} data-testid="edit-influencer">
              <Pencil className="mr-1.5 h-4 w-4" /> Szerkesztés
            </Button>
            <Button
              variant="ghost" size="sm" onClick={removeInfluencer}
              className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
              data-testid="delete-influencer"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-3">
          <Field label="E-mail" value={item.email} icon={Mail} />
          <Field label="Telefon" value={item.phone} icon={Phone} mono />
          <Field label="Forgatások száma" value={item.shoot_count} mono />
        </div>

        {item.platform_links?.length > 0 && (
          <div className="mt-6">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Platform linkek
            </h3>
            <div className="mt-2 flex flex-wrap gap-2">
              {item.platform_links.map((l, idx) => {
                const p = PLATFORM_BY_KEY[l.platform];
                const Icon = p?.icon || LinkIcon;
                return (
                  <a
                    key={idx}
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                    data-testid={`platform-link-${l.platform}`}
                  >
                    <Icon className={`h-3.5 w-3.5 ${p?.tone || ""}`} />
                    {p?.label || l.platform}
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {item.notes && (
          <div className="mt-6">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Megjegyzés</h3>
            <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{item.notes}</p>
          </div>
        )}
      </section>

      {/* Shoots */}
      <section className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clapperboard className="h-4 w-4 text-slate-500" />
            <h2 className="text-sm font-semibold tracking-tight text-slate-700">Forgatások</h2>
            <Badge variant="secondary">{item.shoots?.length || 0}</Badge>
          </div>
          <Button size="sm" onClick={() => { setEditingShoot(null); setOpenShoot(true); }} data-testid="new-shoot-button" className="gap-1.5">
            <Plus className="h-4 w-4" /> Új forgatás
          </Button>
        </div>

        <div className="mt-4 space-y-2">
          {(item.shoots || []).length === 0 ? (
            <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50/40 py-6 text-center text-sm text-slate-400">
              Még nincs rögzített forgatás.
            </p>
          ) : (
            (item.shoots || [])
              .slice()
              .sort((a, b) => (b.scheduled_at || "").localeCompare(a.scheduled_at || ""))
              .map((s) => (
                <div
                  key={s._id}
                  data-testid={`shoot-row-${s._id}`}
                  className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 hover:border-slate-300"
                >
                  <div className="flex min-w-[160px] items-center gap-2 text-sm font-medium text-slate-800">
                    <Calendar className="h-3.5 w-3.5 text-slate-400" />
                    {formatDateTimeHu(s.scheduled_at)}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-600">
                    <Users className="h-3.5 w-3.5 text-slate-400" />
                    {s.people_count} fő
                  </div>
                  <div className="flex flex-1 items-center gap-1.5 text-xs text-slate-600">
                    <Utensils className="h-3.5 w-3.5 text-slate-400" />
                    <span className="truncate">{s.consumption}</span>
                  </div>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => { setEditingShoot(s); setOpenShoot(true); }}
                    className="text-slate-500 hover:text-slate-900"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => removeShoot(s._id)}
                    className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))
          )}
        </div>
      </section>

      <InfluencerFormDialog
        open={openEdit}
        onOpenChange={setOpenEdit}
        initial={item}
        onSaved={() => { setOpenEdit(false); load(); }}
      />
      <ShootFormDialog
        influencerId={id}
        open={openShoot}
        onOpenChange={setOpenShoot}
        initial={editingShoot}
        onSaved={() => { setOpenShoot(false); load(); }}
      />
    </div>
  );
}

const Field = ({ label, value, icon: Icon, mono }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`mt-0.5 flex items-center gap-1.5 text-sm font-medium text-slate-800 ${mono ? "tabular-nums" : ""}`}>
      {Icon && <Icon className="h-3.5 w-3.5 text-slate-400" />}
      {value || "—"}
    </div>
  </div>
);

// ---------- Shoot form dialog ----------
function ShootFormDialog({ influencerId, open, onOpenChange, initial, onSaved }) {
  const isEdit = Boolean(initial);
  const [scheduledAt, setScheduledAt] = useState("");
  const [peopleCount, setPeopleCount] = useState(1);
  const [consumption, setConsumption] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setScheduledAt(initial?.scheduled_at || "");
    setPeopleCount(initial?.people_count || 1);
    setConsumption(initial?.consumption || "");
    setNotes(initial?.notes || "");
  }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    if (!scheduledAt) { toast.error("Időpont kötelező."); return; }
    if (!consumption.trim()) { toast.error("Fogyasztás megadása kötelező."); return; }
    setSubmitting(true);
    try {
      const payload = {
        scheduled_at: scheduledAt,
        people_count: peopleCount,
        consumption: consumption.trim(),
        notes: notes.trim() || null,
      };
      if (isEdit) {
        await api.patch(`/influencers/${influencerId}/shoots/${initial._id}`, payload);
        toast.success("Forgatás frissítve.");
      } else {
        await api.post(`/influencers/${influencerId}/shoots`, payload);
        toast.success("Forgatás rögzítve.");
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Mentés sikertelen.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="shoot-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Forgatás szerkesztése" : "Új forgatás"}</DialogTitle>
          <DialogDescription>Maximum 2 fő vehet részt egy forgatáson.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-600">Időpont *</label>
            <DateTimeInput value={scheduledAt} onChange={setScheduledAt} data-testid="shoot-scheduled-at" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-600">Hány fő? *</label>
            <div className="flex gap-2">
              {[1, 2].map((n) => (
                <button
                  type="button"
                  key={n}
                  onClick={() => setPeopleCount(n)}
                  data-testid={`shoot-people-${n}`}
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-sm font-medium ${
                    peopleCount === n
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <Users className="h-4 w-4" /> {n} fő
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-600">Fogyasztás *</label>
            <Textarea
              value={consumption}
              onChange={(e) => setConsumption(e.target.value)}
              rows={3}
              placeholder="Pl. 2 hamburger menü + 2 üdítő"
              data-testid="shoot-consumption"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-600">Megjegyzés</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Pl. tervezett tartalom típusa, hivatkozások..."
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Mégse</Button>
            <Button type="submit" disabled={submitting} data-testid="shoot-submit">
              {submitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              {isEdit ? "Mentés" : "Hozzáadás"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
